/**
 * Pay link (INV-02 "Pay link"): the current link id, the one-time reveal after a
 * rotation, and the copy control.
 *
 * The contract is explicit and this panel obeys it rather than papering over it:
 * the link TEXT is returned exactly once — at issue and at rotate — and is never
 * re-readable. So there is nothing here that pretends to copy "the current
 * link": the copy control exists only inside the reveal that a rotation just
 * produced, the URL is held in component memory only (never a query string,
 * never a log, never storage), and the panel says out loud that it will not come
 * back — `DETAIL_COPY.rotateMachineryGap` is that statement, and it is rendered
 * as part of the panel rather than left to a reviewer to infer.
 */

import { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import type { ApiErrorInfo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { copyToClipboard } from "@/i18n/copyable-reference";
import { idempotencyKey } from "@/lib/api";
import { rotatePayLink } from "@/features/invoicing/api";
import type { Invoice, PayLinkReveal } from "@/features/invoicing/types";
import { DETAIL_COPY } from "./copy";
import { RefusalNotice } from "./refusal";
import { useInvoicingScope } from "@/features/invoicing/invoices-list/use-invoicing-scope";


export function PayLinkPanel({
  invoice,
  onChanged,
}: {
  invoice: Invoice;
  /** Re-reads the invoice after a rotation so the link id on screen is the new one. */
  onChanged: () => void;
}) {
  const canRotate = useInvoicingScope("invoice:issue");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ApiErrorInfo | null>(null);
  const [reveal, setReveal] = useState<PayLinkReveal | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  // One key per rotation intent: the same key is reused if the operator retries
  // the same rotation, so the service replays its first result instead of
  // minting a second token. Cleared on success, because the next rotation is a
  // different intent and must not be deduplicated into this one.
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!canRotate) {
      setOpen(false);
      setReveal(null);
      setCopyState("idle");
      keyRef.current = null;
    }
  }, [canRotate]);

  async function rotate() {
    if (!canRotate || busy) return;
    setBusy(true);
    setRefusal(null);
    if (!keyRef.current) keyRef.current = idempotencyKey("invoicing_pay_link_rotate");
    const result = await rotatePayLink(invoice.invoiceId, { idempotencyKey: keyRef.current });
    setBusy(false);
    if (!result.ok) {
      setRefusal(result.error);
      return;
    }
    keyRef.current = null;
    setReveal(result.data);
    setCopyState("idle");
    setOpen(false);
    onChanged();
  }

  async function copy() {
    if (!reveal) return;
    const ok = await copyToClipboard(reveal.payUrl);
    setCopyState(ok ? "copied" : "failed");
  }

  const linkId = invoice.payLinkId ?? null;

  return (
    <section aria-labelledby="invoice-pay-link-heading" className="mb-4 rounded-lg border border-line bg-surface px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="invoice-pay-link-heading" className="text-sm font-bold text-ink">
            {DETAIL_COPY.payLinkHeading}
          </h2>
          {linkId ? (
            <p className="mt-1 flex flex-wrap items-baseline gap-2 text-sm">
              <span className="text-muted">{DETAIL_COPY.payLinkIdLabel}</span>
              <code data-testid="invoice-pay-link-id" dir="ltr" className="break-all font-mono text-[13px] text-ink">
                {linkId}
              </code>
            </p>
          ) : (
            <p data-testid="invoice-pay-link-none" className="mt-1 text-sm text-muted">
              {DETAIL_COPY.payLinkNone}
            </p>
          )}
          <p className="mt-1 max-w-[560px] text-xs leading-relaxed text-muted">
            {DETAIL_COPY.payLinkIdNote}
          </p>
        </div>
        {canRotate && (
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)} disabled={busy} data-testid="invoice-rotate-pay-link">
          <RotateCcw aria-hidden />
          {DETAIL_COPY.rotateCta}
        </Button>
        )}
      </div>

      {reveal && (
        <div
          data-testid="pay-link-reveal"
          className="mt-3 rounded-md border border-warning-border bg-warning-soft px-3.5 py-3"
        >
          <div className="text-xs font-bold text-warning">{DETAIL_COPY.rotateShownOnceTitle}</div>
          <p className="mt-1 max-w-[560px] text-xs leading-relaxed text-warning">
            {DETAIL_COPY.rotateShownOnceBody}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-warning">{DETAIL_COPY.payUrlLabel}</span>
            <code data-testid="pay-link-url" dir="ltr" className="min-w-0 break-all font-mono text-[13px] text-ink">
              {reveal.payUrl}
            </code>
            <Button variant="secondary" size="sm" onClick={() => void copy()} data-testid="pay-link-copy">
              {copyState === "copied"
                ? DETAIL_COPY.copiedLabel
                : copyState === "failed"
                  ? DETAIL_COPY.copyFailedLabel
                  : DETAIL_COPY.copyCta}
            </Button>
          </div>
          <p className="mt-1.5 font-mono text-[11px] text-warning">
            {reveal.payLinkId} · {reveal.createdAt}
          </p>
        </div>
      )}

      <p className="mt-3 max-w-[620px] text-xs leading-relaxed text-muted">
        {DETAIL_COPY.rotateMachineryGap}
      </p>

      <Modal
        open={open && canRotate}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setRefusal(null);
        }}
        title={DETAIL_COPY.rotateTitle}
        description={DETAIL_COPY.rotateBody}
        footer={
          <>
            <Button variant="ghost" size="md" onClick={() => setOpen(false)} disabled={busy}>
              {DETAIL_COPY.rotateCancel}
            </Button>
            <Button variant="primary" size="md" onClick={() => void rotate()} disabled={busy} data-testid="pay-link-rotate-confirm">
              {DETAIL_COPY.rotateConfirm}
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-muted">{DETAIL_COPY.rotateHint}</p>
        {refusal && (
          <RefusalNotice className="mt-3" error={refusal} title={DETAIL_COPY.refusedTitle} />
        )}
      </Modal>
    </section>
  );
}
